import { getDashboardContacts, getDashboardEmailContacts } from '../../api/axios';
import { useState, useEffect, useMemo } from 'react';
import { perc, nf } from '../../utilities/Utils'
import useAuth from '../../hooks/useAuth';
import LoadingContainer from '../../components/LoadingContainer';

const getCountValue = (entry) => entry?.contact_count ?? entry?.count ?? 0;

const DistributionList = ({ items, emptyLabel, valueFormatter = nf, valueSuffix = '', tone = 'red' }) => {
    if (!items.length) {
        return <div className="dashboard-empty">{emptyLabel}</div>;
    }

    const maxValue = Math.max(...items.map((item) => item.value), 0);

    return (
        <div className={`dashboard-distribution dashboard-distribution--${tone}`}>
            {items.map((item) => {
                const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 4) : 0;

                return (
                    <div className={`dashboard-distribution__row ${item.fillClass ?? ''}`.trim()} key={item.key}>
                        <div className="dashboard-distribution__meta">
                            <span className="dashboard-distribution__label">{item.label}</span>
                            <span className="dashboard-distribution__value">{valueFormatter(item.value)}{valueSuffix}</span>
                        </div>
                        <div className="dashboard-distribution__track">
                            <div className="dashboard-distribution__fill" style={{ width: `${width}%` }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const MetricCard = ({ label, value, detail, accent = 'red' }) => (
    <article className={`dashboard-metric dashboard-metric--${accent}`}>
        <span className="dashboard-metric__label">{label}</span>
        <strong className="dashboard-metric__value">{value}</strong>
        {detail ? <span className="dashboard-metric__detail">{detail}</span> : null}
    </article>
);

const getBrandFillClass = (brandId, brandName) => {
    const normalizedBrandId = String(brandId ?? '');
    const normalizedBrandName = String(brandName ?? '').trim().toLowerCase();

    if (normalizedBrandId === '1' || normalizedBrandName === 'royal canin') {
        return 'dashboard-distribution__row--royal-canin';
    }

    if (normalizedBrandId === '2' || normalizedBrandName === 'eukanuba') {
        return 'dashboard-distribution__row--eukanuba';
    }

    if (normalizedBrandId === '3' || normalizedBrandName === 'mi vet shop') {
        return 'dashboard-distribution__row--mi-vet-shop';
    }

    return '';
};

const DashboardTable = (props) => {

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const { auth } = useAuth();

    const [contacts, setContacts] = useState(0);
    const [contactTypesSummary, setContactTypesSummary] = useState([]);
    const [contactsByBrand, setContactsByBrand] = useState([]);

    const [email_contacts, setEmailContacts] = useState(0);
    const [ec_unique_contacts, setECUniqueContacts] = useState(0);
    const [ec_unique_clicks, setECUniqueClicks] = useState(0);
    const [ec_unsubscribed, setECUnsubscribed] = useState(0);
    const [ec_opened, setECOpened] = useState(0);
    const [ec_unique_email_contacts, setECUniqueEmailContacts] = useState(0);
    const [ec_bounced, setECBounced] = useState(0);

    const [ec_open_rate, setECOpenRate] = useState(0);
    const [ec_ctr, setECCTR] = useState(0);
    const [ec_ctor, setECCTOR] = useState(0);

    const [ec_royal_canin, setECRoyalCanin] = useState(0);
    const [ec_eukanuba, setECEukanuba] = useState(0);
    const [ec_mi_vet_shop, setECMiVetShop] = useState(0);

    const [emailContactTypesSummary, setEmailContactTypesSummary] = useState([]);

    const paramsKey = useMemo(() => JSON.stringify(props?.params ?? {}), [props?.params]);
    const dashboardParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);

    useEffect(() => {

        const fetchData = async () => {

            setIsLoading(true);

            try {

                const [
                    contactsResponse,
                    contactsByTypeResponse,
                    contactsByBrandResponse,
                ] = await Promise.all([
                    getDashboardContacts({
                        auth,
                        params: { ...dashboardParams },
                    }),
                    getDashboardContacts({
                        auth,
                        params: { ...dashboardParams, byContactType: true },
                    }),
                    getDashboardContacts({
                        auth,
                        params: { ...dashboardParams, byBrand: true },
                    }),
                ]);

                const totalContactsCount = contactsResponse?.count ?? 0;
                setContacts(totalContactsCount);
                setContactTypesSummary(Array.isArray(contactsByTypeResponse?.results) ? contactsByTypeResponse.results : []);
                setContactsByBrand(Array.isArray(contactsByBrandResponse?.results) ? contactsByBrandResponse.results : []);

                const [
                    emailContactsResponse,
                    uniqueContactsResponse,
                    uniqueEmailContactsResponse,
                    uniqueClicksResponse,
                    unsubscribedResponse,
                    openedResponse,
                    bouncedResponse,
                    royalCaninResponse,
                    eukanubaResponse,
                    miVetShopResponse,
                    emailContactTypesResponse,
                ] = await Promise.all([
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, filter: 'ec_unique_contacts' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, filter: 'ec_unique_email_contacts' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, filter: 'ec_unique_clicks' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, status: 'unsubscribed' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, status: 'opened' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, status: 'bounced' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, idBrand: '1' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, idBrand: '2' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, idBrand: '3' },
                    }),
                    getDashboardEmailContacts({
                        auth,
                        params: { ...dashboardParams, byContactType: true },
                    }),
                ]);

                const totalEmailContacts = emailContactsResponse?.count ?? 0;
                const uniqueContactsCount = uniqueContactsResponse?.count ?? 0;
                const uniqueEmailContactsCount = uniqueEmailContactsResponse?.count ?? 0;
                const uniqueClicksCount = uniqueClicksResponse?.count ?? 0;
                const unsubscribedCount = unsubscribedResponse?.count ?? 0;
                const openedCount = openedResponse?.count ?? 0;
                const bouncedCount = bouncedResponse?.count ?? 0;

                setEmailContacts(totalEmailContacts);
                setECUniqueContacts(uniqueContactsCount);
                setECUniqueEmailContacts(uniqueEmailContactsCount);
                setECUniqueClicks(uniqueClicksCount);
                setECUnsubscribed(unsubscribedCount);
                setECOpened(openedCount);
                setECBounced(bouncedCount);

                setECOpenRate(perc(totalEmailContacts, openedCount));
                setECCTR(perc(totalEmailContacts, uniqueClicksCount));
                setECCTOR(perc(openedCount, uniqueClicksCount));

                setECRoyalCanin(royalCaninResponse?.count ?? 0);
                setECEukanuba(eukanubaResponse?.count ?? 0);
                setECMiVetShop(miVetShopResponse?.count ?? 0);
                setEmailContactTypesSummary(Array.isArray(emailContactTypesResponse?.results) ? emailContactTypesResponse.results : []);

                setError(null);
            } catch (fetchError) {
                setError(fetchError);
            }

            setIsLoading(false);
        };

        fetchData();

    }, [auth, dashboardParams]);

    const ownerWithPetCount = useMemo(() => {
        const owners = contactTypesSummary.find((entry) => entry.segment_key === 'owner_with_pet' || entry.contact_type_name === 'Dueño de mascota');
        return getCountValue(owners);
    }, [contactTypesSummary]);

    const contactMix = useMemo(() => contactTypesSummary.map((entry) => ({
        key: entry.segment_key ?? entry.contact_type_id ?? entry.id ?? entry.contact_type_name,
        label: entry.contact_type_name ?? entry.label,
        value: getCountValue(entry),
    })), [contactTypesSummary]);

    const contactBrands = useMemo(() => contactsByBrand.map((entry) => ({
        key: entry.brand_id ?? entry.id ?? entry.brand_name,
        label: (entry.brand_id === 0 || entry.brand_id === '0' || entry.brand_name === '0' || !entry.brand_name) ? 'Sin Marca' : entry.brand_name,
        value: getCountValue(entry),
        fillClass: getBrandFillClass(entry.brand_id, entry.brand_name),
    })), [contactsByBrand]);

    const emailContactMix = useMemo(() => emailContactTypesSummary.map((entry) => ({
        key: entry.segment_key ?? entry.contact_type_id ?? entry.id ?? entry.contact_type_name,
        label: entry.contact_type_name ?? entry.label,
        value: getCountValue(entry),
    })), [emailContactTypesSummary]);

    const emailBrands = useMemo(() => ([
        { key: 'royal-canin', label: 'Royal Canin', value: ec_royal_canin, fillClass: getBrandFillClass(1, 'Royal Canin') },
        { key: 'eukanuba', label: 'Eukanuba', value: ec_eukanuba, fillClass: getBrandFillClass(2, 'Eukanuba') },
        { key: 'mi-vet-shop', label: 'Mi Vet Shop', value: ec_mi_vet_shop, fillClass: getBrandFillClass(3, 'Mi Vet Shop') },
    ].filter((entry) => entry.value > 0)), [ec_royal_canin, ec_eukanuba, ec_mi_vet_shop]);

    const funnelItems = useMemo(() => ([
        {
            key: 'sent',
            label: 'Emails enviados',
            value: email_contacts,
            helper: `${nf(ec_unique_email_contacts)} envíos únicos`,
        },
        {
            key: 'opened',
            label: 'Aperturas únicas',
            value: ec_opened,
            helper: `Open Rate ${ec_open_rate}`,
        },
        {
            key: 'clicks',
            label: 'Clicks únicos',
            value: ec_unique_clicks,
            helper: `CTR ${ec_ctr}`,
        },
        {
            key: 'unsubscribed',
            label: 'Desuscripciones',
            value: ec_unsubscribed,
            helper: `${nf(ec_bounced)} rebotes`,
        },
    ]), [email_contacts, ec_unique_email_contacts, ec_opened, ec_open_rate, ec_unique_clicks, ec_ctr, ec_unsubscribed, ec_bounced]);

    const headlineMetrics = [
        {
            label: 'Base de contactos',
            value: nf(contacts),
            detail: ownerWithPetCount > 0 ? `${nf(ownerWithPetCount)} dueños de mascota` : 'Sin segmentación de mascotas para este filtro',
            accent: 'red',
        },
        {
            label: 'Emails enviados',
            value: nf(email_contacts),
            detail: `${nf(ec_unique_email_contacts)} envíos únicos`,
            accent: 'soft-red',
        },
        {
            label: 'Open Rate',
            value: ec_open_rate,
            detail: `${nf(ec_opened)} aperturas únicas`,
            accent: 'gold',
        },
        {
            label: 'CTR',
            value: ec_ctr,
            detail: `${nf(ec_unique_clicks)} clicks únicos`,
            accent: 'ink',
        },
        {
            label: 'Usuarios alcanzados',
            value: nf(ec_unique_contacts),
            detail: `${ec_ctor} click-to-open`,
            accent: 'sand',
        },
    ];

    if (error) {
        return (
            <LoadingContainer isLoading={isLoading}>
                <div className="dashboard-overview">
                    <div className="dashboard-empty">No se pudieron cargar los indicadores.</div>
                </div>
            </LoadingContainer>
        );
    }

    return (
        <LoadingContainer isLoading={isLoading}>
            <div className="dashboard-overview">
                <section className="dashboard-hero">
                    <div className="dashboard-hero__intro">
                        <span className="dashboard-eyebrow">Resumen ejecutivo</span>
                        <h2 className="dashboard-hero__title">Contactos y desempeño de envíos</h2>
                    </div>
                    <div className="dashboard-hero__metrics">
                        {headlineMetrics.map((metric) => (
                            <MetricCard
                                key={metric.label}
                                label={metric.label}
                                value={metric.value}
                                detail={metric.detail}
                                accent={metric.accent}
                            />
                        ))}
                    </div>
                </section>

                <section className="dashboard-grid">
                    <article className="dashboard-panel dashboard-panel--feature">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Audiencia</span>
                                <h3 className="dashboard-panel__title">Composición de contactos</h3>
                            </div>
                            <strong className="dashboard-panel__highlight">{nf(contacts)}</strong>
                        </div>
                        <DistributionList
                            items={contactMix}
                            emptyLabel={isLoading ? 'Cargando...' : 'Sin datos'}
                            tone="red"
                        />
                    </article>

                    <article className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Marca</span>
                                <h3 className="dashboard-panel__title">Contactos por marca</h3>
                            </div>
                        </div>
                        <DistributionList
                            items={contactBrands}
                            emptyLabel={isLoading ? 'Cargando...' : 'Sin datos'}
                            tone="gold"
                        />
                    </article>

                    <article className="dashboard-panel dashboard-panel--feature">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Rendimiento</span>
                                <h3 className="dashboard-panel__title">Funnel de envíos</h3>
                            </div>
                            <div className="dashboard-panel__highlight-group">
                                <span className="dashboard-panel__highlight-label">OR</span>
                                <strong className="dashboard-panel__highlight">{ec_open_rate}</strong>
                            </div>
                        </div>
                        <div className="dashboard-funnel">
                            {funnelItems.map((item, index) => {
                                const width = email_contacts > 0 ? Math.max((item.value / email_contacts) * 100, item.value > 0 ? 8 : 0) : 0;

                                return (
                                    <div className="dashboard-funnel__step" key={item.key}>
                                        <div className="dashboard-funnel__meta">
                                            <span className="dashboard-funnel__index">0{index + 1}</span>
                                            <div>
                                                <div className="dashboard-funnel__label">{item.label}</div>
                                                <div className="dashboard-funnel__helper">{item.helper}</div>
                                            </div>
                                            <strong className="dashboard-funnel__value">{nf(item.value)}</strong>
                                        </div>
                                        <div className="dashboard-funnel__track">
                                            <div className="dashboard-funnel__fill" style={{ width: `${width}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </article>

                    <article className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Performance</span>
                                <h3 className="dashboard-panel__title">Indicadores clave</h3>
                            </div>
                        </div>
                        <div className="dashboard-scoreboard">
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">Open Rate</span>
                                <strong className="dashboard-scoreboard__value">{ec_open_rate}</strong>
                            </div>
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">CTR</span>
                                <strong className="dashboard-scoreboard__value">{ec_ctr}</strong>
                            </div>
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">CTOR</span>
                                <strong className="dashboard-scoreboard__value">{ec_ctor}</strong>
                            </div>
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">Desuscripciones</span>
                                <strong className="dashboard-scoreboard__value">{nf(ec_unsubscribed)}</strong>
                            </div>
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">Rebotes</span>
                                <strong className="dashboard-scoreboard__value">{nf(ec_bounced)}</strong>
                            </div>
                            <div className="dashboard-scoreboard__item">
                                <span className="dashboard-scoreboard__label">Usuarios únicos</span>
                                <strong className="dashboard-scoreboard__value">{nf(ec_unique_contacts)}</strong>
                            </div>
                        </div>
                    </article>

                    <article className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Marca</span>
                                <h3 className="dashboard-panel__title">Envíos por marca</h3>
                            </div>
                        </div>
                        <DistributionList
                            items={emailBrands}
                            emptyLabel={isLoading ? 'Cargando...' : 'Sin datos'}
                            tone="ink"
                        />
                    </article>

                    <article className="dashboard-panel">
                        <div className="dashboard-panel__header">
                            <div>
                                <span className="dashboard-panel__kicker">Audiencia</span>
                                <h3 className="dashboard-panel__title">Envíos por tipo de contacto</h3>
                            </div>
                        </div>
                        <DistributionList
                            items={emailContactMix}
                            emptyLabel={isLoading ? 'Cargando...' : 'Sin datos'}
                            tone="sand"
                        />
                    </article>
                </section>
            </div>
        </LoadingContainer>
    )
}

export default DashboardTable
